import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Profile.css';

const Profile = () => {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    height: '',
    currentWeight: '',
    targetWeight: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Функция для форматирования даты в формат YYYY-MM-DD для input[type="date"]
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        dateOfBirth: formatDateForInput(user.dateOfBirth),
        gender: user.gender || '',
        height: user.height ? Math.round(user.height).toString() : '',
        currentWeight: user.currentWeight ? Math.round(user.currentWeight).toString() : '',
        targetWeight: user.targetWeight ? Math.round(user.targetWeight).toString() : ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Для числовых полей веса и роста - только целые числа
    if ((name === 'currentWeight' || name === 'targetWeight' || name === 'height') && value !== '') {
      // Удаляем все нечисловые символы и дробную часть
      const cleanValue = value.replace(/[^\d]/g, '');
      if (cleanValue === '') {
        setFormData({ ...formData, [name]: '' });
        return;
      }
      const intValue = parseInt(cleanValue, 10);
      if (isNaN(intValue) || intValue < 0) return;
      setFormData({ ...formData, [name]: intValue.toString() });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Убираем ошибку при изменении поля
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Валидация email
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Введите корректный email';
      }
    }

    // Валидация даты рождения (не младше 5 лет)
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - 5);

      if (birthDate > minDate) {
        newErrors.dateOfBirth = 'Возраст должен быть не менее 5 лет';
      }

      if (birthDate > today) {
        newErrors.dateOfBirth = 'Дата рождения не может быть в будущем';
      }
    }

    // Валидация веса (целые числа)
    if (formData.currentWeight && (isNaN(formData.currentWeight) || parseFloat(formData.currentWeight) % 1 !== 0)) {
      newErrors.currentWeight = 'Вес должен быть целым числом';
    }

    if (formData.targetWeight && (isNaN(formData.targetWeight) || parseFloat(formData.targetWeight) % 1 !== 0)) {
      newErrors.targetWeight = 'Вес должен быть целым числом';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProfile = async () => {
    // Валидация перед отправкой
    if (!validateForm()) {
      alert('Пожалуйста, исправьте ошибки в форме');
      return;
    }

    try {
      setLoading(true);

      // Подготовим данные для отправки - уберем phone и преобразуем числовые поля
      const dataToSend = {
        name: formData.name,
        email: formData.email,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        height: formData.height ? parseInt(formData.height, 10) : undefined,
        currentWeight: formData.currentWeight ? parseInt(formData.currentWeight, 10) : undefined,
        targetWeight: formData.targetWeight ? parseInt(formData.targetWeight, 10) : undefined,
      };

      // Убираем undefined значения и пустые строки
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === undefined || dataToSend[key] === '') {
          delete dataToSend[key];
        }
      });

      await updateProfile(dataToSend);
      alert('Профиль успешно обновлен');
    } catch (error) {
      console.error('Profile update error:', error);
      alert('Ошибка сохранения профиля: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Вы уверены, что хотите выйти из аккаунта?')) {
      logout();
      navigate('/');
    }
  };

  return (
    <div className="profile">
      <div className="profile-header">
        <h1>Личный кабинет</h1>
        <p>Обновите свои данные для персонализированного опыта</p>
      </div>

      <div className="profile-content">
        <div className="section-content">
          <div className="form-grid">
            <div className="form-group">
              <label>Имя</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Введите ваше имя"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Введите email"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Телефон</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled
                placeholder="Телефон (не редактируется)"
              />
            </div>
            <div className="form-group">
              <label>Дата рождения</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 5)).toISOString().split('T')[0]}
                className={errors.dateOfBirth ? 'error' : ''}
              />
              {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
            </div>
            <div className="form-group">
              <label>Пол</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
              >
                <option value="">Не указан</option>
                <option value="male">Мужской</option>
                <option value="female">Женский</option>
              </select>
            </div>
            <div className="form-group">
              <label>Рост (см)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="height"
                value={formData.height}
                onChange={handleInputChange}
                placeholder="Например, 175"
              />
            </div>
            <div className="form-group">
              <label>Текущий вес (кг)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="currentWeight"
                value={formData.currentWeight}
                onChange={handleInputChange}
                placeholder="Например, 70"
                className={errors.currentWeight ? 'error' : ''}
              />
              {errors.currentWeight && <span className="error-message">{errors.currentWeight}</span>}
            </div>
            <div className="form-group">
              <label>Целевой вес (кг)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="targetWeight"
                value={formData.targetWeight}
                onChange={handleInputChange}
                placeholder="Например, 65"
                className={errors.targetWeight ? 'error' : ''}
              />
              {errors.targetWeight && <span className="error-message">{errors.targetWeight}</span>}
            </div>
          </div>

          <div className="profile-actions">
            <button
              className="btn-primary save-btn"
              onClick={handleSaveProfile}
              disabled={loading}
            >
              {loading ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
            <button
              className="btn-logout"
              onClick={handleLogout}
            >
              Выйти из аккаунта
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
